import React, { useEffect, useRef, useState } from "react";

import AvatarCaminhante from "./AvatarCaminhante";
import { destinoDaCamera, noEstaNaTela } from "./camera";
import { duracaoDaCaminhada } from "./caminhada";
import {
  ehClicavel,
  estadoDoNo,
  fimDaJanelaAtual,
  indiceFaseAtual,
  temFasesEscondidas
} from "./motorFases";
import { useNavegacaoDaPista } from "./navegacaoDaPista";
import NoTrilha from "./NoTrilha";
import PassagemLeva from "./PassagemLeva";
import { faseEntregaCarta } from "./recompensaDaFase";
import { ehFaseRara } from "./recompensas";
import SetaDeslizante from "./SetaDeslizante";

// Geometria da trilha, virada para o eixo HORIZONTAL em 02/08/2026. O percurso
// cresce para a DIREITA e rola dentro da própria pista, em vez de crescer para
// cima e empurrar a página do AVA.
const VIEWBOX_ALTURA = 400;
// O PASSO entre dois nós, e ele é FIXO — decisão do Kilson em 19/08/2026.
//
// Era elástico: um piso de 184 e um teto de 320, com os nós se espalhando até
// preencher a pista. O efeito, medido numa pista de 1658px, é que o passo
// encolhia à medida que a trilha crescia:
//
//     fases      2-5      6      8     10+
//     passo      320    288    205     184   <- só aqui a faixa passava a rolar
//
// Ou seja, a trilha "crescia" comprimindo a si mesma, e o aluno via o percurso
// da leva 2 mais apertado que o da leva 1 — foi assim que o defeito foi
// relatado. E a rolagem, que é o recurso que existe para trilha longa, só
// entrava depois de o passo já ter perdido 43%.
//
// Com passo fixo, a faixa cresce e ROLA desde a primeira fase que não couber, e
// a câmera passa a ter função desde cedo. Ganha-se também uma coisa que o
// elástico impedia por construção: a caminhada do explorador tem sempre a mesma
// velocidade, porque `duracaoDaCaminhada` deriva a duração da distância — e a
// distância agora é sempre a mesma.
//
// 320 e não outro número porque era o TETO de antes: toda trilha de até 5 fases
// numa pista de 1658px já usava exatamente 320, então elas ficam idênticas ao que
// foram aprovadas, e só as de 6 em diante mudam. O que sobrar da pista continua
// virando folga simétrica nas pontas (ver `distribuir`), e não vazio à direita.
//
// O valor tem um piso implícito que não se deve baixar sem medir de novo: abaixo
// de ~184 os rótulos de duas linhas começam a se tocar, que é o que se via no
// print de 8 fases com o passo em 205.
const ESPACO = 320;
// Folga nas duas pontas. À esquerda ela acomoda metade do nó (36px, o medalhão de
// 72) mais metade do rótulo (70px); à direita, a faixa de névoa que avisa que a
// trilha continua.
const PADDING = 110;
// O avatar fica ACIMA do nó, e não ao lado.
//
// No eixo vertical ele tinha ido para o lado justamente para não cobrir o rótulo
// do nó seguinte. Aqui a lógica se inverte: o rótulo é desenhado ABAIXO do seu nó
// (`top: 100%`) e o vizinho está ao LADO, então o lugar livre é acima.
const AVATAR_ACIMA_DO_NO = 70;

// ---------------------------------------------------------------------------
// A ESTRADA É UMA CURVA, não seis posições — e a CURVA É DA REGIÃO.
//
// Havia aqui uma tabela de 6 alturas, uma por nó, e ela só funcionava enquanto o
// cenário fosse esticado junto com o espaçamento — a arte tinha de repetir a cada
// 6 nós para os nós caírem sempre nos mesmos pontos dela. Foi assim que a
// ilustração acabou deformada: esticar a LARGURA do cenário sem esticar a altura
// achata os cactos e alonga as pedras. Reportado com um print.
//
// Com o perfil como curva, o nó pode cair em QUALQUER ponto do cenário e ainda
// achar a altura certa da estrada. Isso liberta as três coisas que antes
// competiam entre si: a arte fica sem distorção (`auto 100%` preserva a
// proporção), os nós se espalham pela pista, e continuam sobre o leito.
//
// Até 05/08/2026 as 48 amostras e a proporção do tile eram CONSTANTES DESTE
// ARQUIVO, e com elas o núcleo conhecia a Caatinga — números que descrevem uma
// ilustração específica, num módulo que não deveria saber qual bioma está na tela.
// Hoje vêm na prop `tabuleiro`, do descritor da região
// (`conteudo/regioes/<id>/index.js`), e quem os produz e CONFERE são
// `ferramentas-da-trilha/gerar-fundo-trilha.py` e
// `ferramentas-da-trilha/gerar-perfil-estrada.py`.

// Proporção usada quando a região não diz a sua. Não é a de nenhuma arte: é 2:1,
// que dá um cenário de largura plausível para o cálculo de onde ele se repete.
const PROPORCAO_NEUTRA = 2;
// Altura usada quando não há perfil: o meio da faixa. Degradação DELIBERADA e
// visível — a trilha aparece com os nós em linha reta, o aluno faz a atividade, e
// quem esqueceu de passar a região vê na hora que esqueceu.
const ALTURA_NEUTRA = 50;

/**
 * Altura da estrada, em % da faixa, num ponto qualquer do cenário.
 *
 * @param {number} fracao posição dentro do cenário, 0 a 1 (fora disso, cicla)
 * @param {number[]} [perfil] as 48 amostras da região; sem elas, o meio da faixa
 */
export function alturaDaEstrada(fracao, perfil) {
  if (!perfil || perfil.length === 0) return ALTURA_NEUTRA;

  const n = perfil.length;
  // `%` de negativo é negativo em JS, e o cenário cicla nos dois sentidos.
  const t = (((fracao % 1) + 1) % 1) * n;
  const i = Math.floor(t);
  const proximo = (i + 1) % n;
  const peso = t - i;
  // Interpola em vez de arredondar para a amostra mais próxima: entre duas
  // vizinhas há até 5,1pp, e saltar isso poria o nó na borda do leito.
  const valor = perfil[i] * (1 - peso) + perfil[proximo] * peso;
  return Math.round(valor * 100) / 100;
}

// Largura do percurso: a que ele TEM, agora que o passo é fixo. Era "a que ele
// teria com o espaçamento mínimo", quando o passo ainda era elástico.
export function larguraDoPercurso(ateFase) {
  return PADDING * 2 + (ateFase - 1) * ESPACO;
}

/**
 * Distribui os nós pela faixa e devolve a posição de cada um.
 *
 * O passo é FIXO (`ESPACO`): a trilha ocupa o que precisa e a faixa cresce
 * quando o percurso não cabe na pista, momento em que ela passa a rolar. Não há
 * mais espalhamento elástico — ver o comentário de `ESPACO` para o porquê e para
 * a tabela do que mudou.
 *
 * Quando o percurso é MENOR que a pista, o que sobra vira folga simétrica nas
 * pontas. É o que resolve o problema que o espalhamento resolvia: com poucas
 * fases os nós ficavam amontoados no terço esquerdo, e a faixa lia como trilha
 * inacabada. Centrado, lê como enquadramento.
 *
 * @param {number} quantos  nós na janela atual
 * @param {number} [larguraFaixa] medida em px; ausente = a faixa é o percurso
 * @param {number} [larguraCenario] px de um cenário; ausente = usa a nominal
 * @param {number[]} [perfil] a curva da estrada da região (ver alturaDaEstrada)
 */
export function distribuir(quantos, larguraFaixa, larguraCenario, perfil) {
  const espaco = quantos > 1 ? ESPACO : 0;
  const percurso = PADDING * 2 + (quantos - 1) * espaco;

  // A faixa é o maior entre o percurso e a pista: cabendo, ela acompanha a pista
  // e o percurso fica centrado; não cabendo, ela cresce e a pista rola.
  const largura = Math.max(percurso, larguraFaixa || 0);
  const folga = Math.max(0, (largura - percurso) / 2);

  // Sem medição do cenário, a nominal: a de uma faixa de 520px de altura.
  const cenario = larguraCenario || PROPORCAO_NEUTRA * 520;

  return {
    largura,
    espaco,
    nos: Array.from({ length: quantos }, (_, i) => {
      const x = folga + PADDING + i * espaco;
      return {
        numero: i + 1,
        x,
        // % da faixa: sobrevive a um resize antes de o React re-renderizar.
        esquerdaPct: Math.round((x / largura) * 1000000) / 10000,
        topoPct: alturaDaEstrada(x / cenario, perfil)
      };
    })
  };
}

// Segmentos retos entre os nós. Eram curvas de Bézier até o redesign de 28/07:
// com os nós mais próximos da linha da estrada a curva ficava praticamente reta
// de qualquer jeito, e a linha reta acompanha melhor a estrada desenhada no fundo.
export function construirPath(pontos) {
  if (pontos.length === 0) return "";
  let d = `M ${pontos[0].x} ${pontos[0].y}`;
  for (let i = 1; i < pontos.length; i += 1) {
    d += ` L ${pontos[i].x} ${pontos[i].y}`;
  }
  return d;
}

// Até onde a janela chega para um modelo — o mesmo cálculo do corpo do
// componente, extraído porque o efeito da passagem de leva precisa comparar a
// janela do modelo NOVO com a do que está na tela.
function janelaDe(modelo) {
  if (!modelo || !modelo.fases) return 0;
  // Atividade livre e prévia do professor não têm progressão a revelar: todos os
  // nós já estão disponíveis, portanto todos precisam fazer parte da faixa desde
  // a montagem. Aplicar as levas aqui deixava a segunda metade fora do DOM,
  // apesar de `sequencial: false` dizer que ela estava livre.
  if (!modelo.sequencial) return modelo.totalFases;
  const indice = indiceFaseAtual(modelo.fases, modelo.sequencial);
  const fase = indice === -1 ? modelo.totalFases : indice + 1;
  return fimDaJanelaAtual(fase, modelo.totalFases);
}

function Tabuleiro({
  trilha,
  avatar,
  onAbrirFase,
  adiarPassagem = false,
  // A arte desta região: `{ proporcao, perfilDaEstrada }` do descritor. Ausente, o
  // tabuleiro desenha os nós em linha reta e o CSS cai no cenário padrão — a
  // trilha funciona, e a falta se vê.
  //
  // `|| {}` e não valor padrão de parâmetro: o padrão só vale para `undefined`, e
  // quem monta isto escreve `regiao && regiao.tabuleiro`, que devolve `null` para
  // região nula.
  tabuleiro,
  // A cabeça do mascote da região, para o medalhão das fases raras. Chega aqui de
  // passagem: quem desenha o nó é `NoTrilha`.
  mascoteCabeca,
  // `{ comuns, raras }`. Só as raras importam aqui — ver `comCard` abaixo.
  sorteio
}) {
  const arte = tabuleiro || {};
  const noAtualRef = useRef(null);
  const pistaRef = useRef(null);
  const jaRolou = useRef(false);
  // Em que fase a câmera viu o explorador da última vez. É o que permite saber
  // quantos nós ele está atravessando agora — e portanto quanto a câmera tem de
  // esperar antes de reenquadrar. `null` na montagem, onde não houve percurso.
  const indiceAnterior = useRef(null);

  // ---------- O tamanho da pista ----------
  // A única coisa que este módulo mede, e ela é necessária: sem saber a largura
  // da pista não há como espalhar os nós até preenchê-la, e sem saber a altura
  // não há como saber onde o cenário se repete (`auto 100%` faz a largura do
  // cenário ser a proporção do tile vezes a altura da faixa).
  //
  // Com ResizeObserver, e não medindo na montagem: a trilha nasce dentro de um
  // `tab-pane` sem área, e a medição feita uma vez ali daria zero para sempre —
  // é armadilha registrada deste módulo. O observer dispara quando a área
  // aparece, e de novo a cada mudança de viewport.
  //
  // Enquanto não há medida (primeiro frame, ou navegador sem a API), `distribuir`
  // cai no espaçamento mínimo e no cenário nominal. Degradação segura: a trilha
  // aparece certa, só não espalhada.
  const [pista, setPista] = useState(null);

  useEffect(() => {
    const alvo = pistaRef.current;
    if (!alvo || typeof ResizeObserver === "undefined") return undefined;

    const medir = () =>
      setPista(atual => {
        const largura = alvo.clientWidth;
        const altura = alvo.clientHeight;
        if (!largura || !altura) return atual;
        // Só troca o objeto quando o número muda: um `setState` a cada frame de
        // resize re-renderiza a trilha inteira à toa.
        if (atual && atual.largura === largura && atual.altura === altura) {
          return atual;
        }
        return { largura, altura };
      });

    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(alvo);
    return () => observador.disconnect();
  }, []);

  // ---------- Passagem de leva ----------
  // O tabuleiro renderiza `exibida`, e não a prop direto. As duas são o mesmo
  // objeto quase todo o tempo; elas se separam quando a leva troca, e é essa
  // separação que permite a troca acontecer atrás da cortina de neblina.
  //
  // Por que o congelamento é da trilha INTEIRA e não só do número da janela: com
  // as fases novas e a janela antiga, a última fase da leva 1 apareceria
  // concluída sem existir a fase seguinte, então nenhum nó seria o atual e o
  // avatar desapareceria — durante o fade de entrada da neblina, ou seja à vista.
  const [exibida, setExibida] = useState(trilha);
  // 'nenhuma' -> 'aguardando' (leva nova em espera, host pediu para adiar)
  //           -> 'ativa' (cortina no ar) -> 'nenhuma'
  const [passagem, setPassagem] = useState("nenhuma");
  // De qual nó o explorador sai na passagem de leva — a fase em que ele estava
  // quando a leva nova chegou.
  //
  // Existe porque a troca de leva é a única mudança do modelo que reescreve as
  // coordenadas de TODOS os nós: o nó 3 de uma leva de 3 fica em 79% da faixa, e o
  // mesmo nó 3 numa leva de 6 fica em 42%. Sem guardar a fase de partida, o avatar
  // recebia de uma vez a fase nova E a faixa nova, e o único jeito de não animar
  // aquele reposicionamento absurdo era pular o avanço inteiro (`semAnimacao`) —
  // que é o defeito: o aluno concluía a fase premiada, fechava a carta, e o
  // explorador já estava no nó seguinte quando a neblina abria. Medido em 03/08
  // com `ferramentas-diagnostico/harness-trilha` (trilha de 6: 870px -> 662px em
  // duas amostras, nenhuma posição intermediária).
  //
  // Com ela, a passagem se separa nas duas coisas que ela sempre foi: o
  // REPOSICIONAMENTO do nó de partida na faixa nova, que acontece atrás da neblina
  // e não é movimento nenhum, e o AVANÇO para o nó seguinte, que espera a tela
  // ficar livre — a mesma regra que a cerimônia do card já obedece.
  const [fasePartida, setFasePartida] = useState(null);
  const pendente = useRef(null);
  // Ref e não estado: só o efeito de scroll abaixo lê isto, e ele NÃO deve
  // re-rodar quando o valor muda — se fosse dependência, desligar a cortina
  // dispararia um segundo scroll depois de a passagem já ter terminado.
  const cobrindoRef = useRef(false);

  useEffect(() => {
    if (trilha === exibida) return;
    // Já há leva nova esperando ou em transição: o modelo novo entra na fila em
    // vez de ser aplicado. Sem isto, um segundo refetch chegando no meio (a
    // página `Do` refetcha a cada modal fechado) cairia no `else` abaixo e
    // trocaria a trilha à vista — justo o que a cortina existe para esconder.
    if (passagem !== "nenhuma") {
      pendente.current = trilha;
      return;
    }
    // Cresceu a janela = abriu leva nova. Só nesse caso vale a cortina: qualquer
    // outra mudança do modelo (uma fase concluída no meio da leva, um refetch que
    // não mudou nada) tem de aparecer na hora — uma cortina de 1,6s por item
    // concluído seria insuportável.
    if (janelaDe(trilha) > janelaDe(exibida)) {
      pendente.current = trilha;
      // A fase de partida sai de `exibida`, a leva que está NA TELA — é de onde o
      // explorador parte. Lida aqui, e não no corpo do componente, porque depois
      // que `exibida` trocar essa informação não existe mais em lugar nenhum.
      const indice = indiceFaseAtual(exibida.fases, exibida.sequencial);
      setFasePartida(indice === -1 ? null : indice + 1);
      setPassagem("aguardando");
    } else {
      setExibida(trilha);
    }
    // `exibida` fora das dependências de propósito: ela é o que este efeito
    // escreve, e reagir à própria escrita faria o efeito rodar de novo com a
    // comparação já resolvida. O gatilho é a prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trilha]);

  // A cortina só começa quando o host libera. É o que faz a transição ser VISTA:
  // no AVA, a fase que abre a leva 2 é a fase rara, então a cerimônia do card
  // abre no mesmo instante em que o modelo novo chega — uma cortina rodando ali
  // aconteceria atrás do scrim da carta, e o aluno veria no máximo um cinza
  // passando. Esperando a carta sair, ele vê o que o protótipo entrega: a tela
  // clareia e a continuação do tabuleiro aparece.
  //
  // A leva fica congelada durante a espera, e isso é um ganho: o tabuleiro atrás
  // da carta não salta de 880 para 1660px enquanto o aluno lê a curiosidade.
  useEffect(() => {
    if (passagem !== "aguardando" || adiarPassagem) return;
    cobrindoRef.current = true;
    setPassagem("ativa");
  }, [passagem, adiarPassagem]);

  function adotarPendente() {
    if (!pendente.current) return;
    setExibida(pendente.current);
    pendente.current = null;
  }

  // A neblina cobriu a tela. Tudo o que acontece daqui é invisível para o aluno:
  // o tabuleiro muda de altura, os nós da leva nova montam, o caminho SVG ganha
  // pontos e o scroll se reposiciona.
  function trocarLevaCoberto() {
    adotarPendente();
  }

  // A cortina terminou de se abrir. Adota de novo porque um refetch pode ter
  // chegado entre "coberto" e agora — nesse caso ele ficou na fila e este é o
  // primeiro momento em que aplicá-lo não estraga a transição.
  //
  // O ref só desliga aqui, e não junto com a troca: o efeito de scroll roda
  // DEPOIS da troca de leva, e precisa ainda ver `true` para rolar instantâneo.
  //
  // E é ESTE commit que solta o avanço guardado do explorador: largando a fase de
  // partida junto com `passagem`, os dois chegam ao avatar no mesmo lote — a fase
  // nova sem `semAnimacao` —, e ele sai do nó de partida (já reposicionado na faixa
  // nova, atrás da neblina) caminhando até o nó seguinte, à vista.
  function fimDaPassagem() {
    adotarPendente();
    cobrindoRef.current = false;
    setFasePartida(null);
    setPassagem("nenhuma");
  }

  const { fases, totalFases, sequencial } = exibida;

  const indiceAtual = indiceFaseAtual(fases, sequencial);
  // Sem nó atual (trilha concluída) a janela é a última — mostra tudo o que o
  // aluno já percorreu na leva final em vez de voltar para a leva 1.
  const faseAtual = indiceAtual === -1 ? totalFases : indiceAtual + 1;
  // Levas revelam o progresso de uma trilha SEQUENCIAL. Numa atividade livre — e
  // na prévia destravada do professor — esconder a segunda leva contradiz o
  // próprio modelo: as fases estão disponíveis, mas nem sequer seriam desenhadas.
  const janelaFim = sequencial
    ? fimDaJanelaAtual(faseAtual, totalFases)
    : totalFases;
  // `null` com a trilha concluída, e não `faseAtual`: aqui a distinção importa,
  // porque `faseAtual` cai na última fase para calcular a janela, e o avatar não
  // deve aparecer plantado sobre ela como se ainda houvesse o que fazer.
  const faseDoAvatar = indiceAtual === -1 ? null : indiceAtual + 1;

  const {
    largura: larguraTabuleiro,
    espaco,
    nos: posicoes
  } = distribuir(
    janelaFim,
    pista && pista.largura,
    pista && (arte.proporcao || PROPORCAO_NEUTRA) * pista.altura,
    arte.perfilDaEstrada
  );

  // Setas, arrastar e encaixe — as três camadas de navegação sem barra de
  // rolagem. O scroll nativo continua por baixo das três (ver ./navegacaoDaPista).
  const navegacao = useNavegacaoDaPista(pistaRef, espaco);

  // O viewBox tem 100 unidades de largura, e não a largura em px: assim o caminho
  // usa as MESMAS porcentagens dos nós, e não há como os dois esticarem em fatores
  // diferentes — que foi o defeito da linha tracejada correndo sozinha pela faixa.
  // `vectorEffect: non-scaling-stroke` no `path` impede que o tracejado se alongue
  // junto com o esticamento não-uniforme.
  const pontosSvg = posicoes.map(p => ({
    x: p.esquerdaPct,
    y: (p.topoPct / 100) * VIEWBOX_ALTURA
  }));

  // Durante a passagem de leva o explorador continua no nó de PARTIDA, mas medido
  // na faixa nova — que é onde ele tem de estar quando a neblina abrir, e não onde
  // a porcentagem antiga o deixaria (79% de uma faixa de 6 nós é o vão entre o 5º
  // e o 6º, ou seja o meio do nada).
  //
  // O nó de partida existe na leva nova por construção: a janela só CRESCE, e ela
  // inclui todos os nós que já estavam desenhados.
  const faseNaFaixa =
    passagem !== "nenhuma" && fasePartida !== null ? fasePartida : faseDoAvatar;
  const posicaoDoAvatar =
    faseNaFaixa !== null ? posicoes[faseNaFaixa - 1] : null;

  // Põe o nó atual à vista dentro da PISTA — quando ele não estiver.
  //
  // No eixo vertical havia uma regra explícita de nunca rolar na montagem: a
  // trilha era uma seção de uma página maior, e rolar sequestraria a posição do
  // aluno, que podia estar lendo o enunciado acima. Com a pista tendo rolagem
  // PRÓPRIA esse motivo desaparece, e se inverte — abrindo em `scrollLeft: 0`, o
  // aluno na fase 8 veria o começo da trilha e nenhum nó atual.
  //
  // Por isso o scroll é escrito na pista em vez de `scrollIntoView`: aquele rola
  // TODO ancestral rolável até a viewport, ou seja mexeria na página do AVA junto.
  // Aqui só a pista se move, em qualquer caso.
  //
  // DUAS REGRAS NOVAS (03/08), e as duas existem para a caminhada aparecer:
  //
  // 1. A câmera tem zona morta (ver ./camera): enquanto o nó atual está na faixa
  //    central da pista, ela não se mexe. Centrar a cada avanço fazia a pista rolar
  //    junto com o explorador e no mesmo sentido — ele percorria 184px de faixa e
  //    22px de tela, ou seja o aluno via o cenário deslizar por baixo de um boneco
  //    parado.
  // 2. Quando precisa reenquadrar, ESPERA a caminhada terminar. Senão o
  //    reenquadramento cairia justamente sobre o único avanço em que ele acontece,
  //    e naquele a caminhada voltaria a ser comida.
  //
  // Nas fases premiadas isso já acontecia de graça: a fase avança quando a carta
  // cobre a tela, então a câmera reenquadrava atrás do scrim e estava parada quando
  // o aluno fechava a carta. Era por isso que carta comum andava e avanço seco não.
  useEffect(() => {
    const pista = pistaRef.current;
    if (!pista) return undefined;

    // As três marcas de "já passei por aqui" são escritas ANTES de qualquer saída
    // antecipada. Com a zona morta, a primeira renderização muitas vezes não rola
    // nada — e marcando só quando rolava, o avanço seguinte se passava por montagem
    // e reenquadrava na hora, em cima da caminhada. Foi o que o teste pegou.
    const primeiraVez = !jaRolou.current;
    jaRolou.current = true;
    const de = indiceAnterior.current;
    indiceAnterior.current = indiceAtual;

    const alvo = noAtualRef.current;
    // Sem nó atual (trilha concluída) não há para onde levar a câmera.
    if (!alvo) return undefined;

    const enquadramento = {
      centroDoNo: alvo.offsetLeft + alvo.offsetWidth / 2,
      larguraDaPista: pista.clientWidth,
      scrollAtual: pista.scrollLeft
    };
    const destino = destinoDaCamera({
      ...enquadramento,
      larguraDaFaixa: larguraTabuleiro
    });
    if (destino === null) return undefined;

    // Na montagem e durante a passagem de leva, instantâneo. Na primeira porque
    // não houve percurso para acompanhar; na cortina porque ela acontece atrás da
    // neblina — um scroll suave ali competiria com a animação e poderia não ter
    // terminado quando ela abrisse.
    const instantaneo = primeiraVez || cobrindoRef.current;

    const mover = () => {
      if (instantaneo || !pista.scrollTo) {
        pista.scrollLeft = destino;
        return;
      }
      pista.scrollTo({ left: destino, behavior: "smooth" });
    };

    if (instantaneo) {
      mover();
      return undefined;
    }

    // Quanto esperar é a duração da caminhada que está acontecendo agora — o mesmo
    // cálculo que o avatar usa (./caminhada), e não um número escrito aqui.
    //
    // Sem caminhada (correção de progresso vinda do servidor, conclusão fora de
    // ordem) não há o que esperar. E sem o nó de destino à vista, esperar seria
    // deixar o explorador caminhar para fora da pista: em tela estreita a zona morta
    // não chega a caber um vão inteiro, e ali a câmera acompanha mesmo.
    const fasesVencidas = de === null ? 0 : Math.abs(indiceAtual - de);
    if (!fasesVencidas || !noEstaNaTela(enquadramento)) {
      mover();
      return undefined;
    }
    const id = setTimeout(mover, duracaoDaCaminhada(fasesVencidas, espaco));
    return () => clearTimeout(id);
    // `espaco` e `larguraTabuleiro` ficam fora das dependências de propósito: eles
    // mudam a cada medição da pista e a cada resize, e reagir a isso reenquadraria
    // a câmera enquanto o aluno arrasta a trilha com o dedo. Quem manda aqui é a
    // troca de fase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indiceAtual]);

  // A leva nova muda a largura da faixa, e com ela o que há para rolar. O
  // listener de scroll não dispara sozinho nesse caso — a posição não mudou, o
  // conteúdo é que cresceu —, então as setas ficariam desatualizadas.
  useEffect(() => {
    navegacao.medirAlcance();
  }, [navegacao, larguraTabuleiro, janelaFim]);

  return (
    /* A PISTA é a janela; a faixa lá dentro é o percurso. É esta divisão que
       cumpre a regra da feature: a página do AVA não pode crescer para o lado, e
       a rolagem tem de ser só daqui.

       `role="region"` com nome porque um container rolável sem nome é um beco
       para quem navega por teclado ou leitor de tela: dá para entrar nele e não
       saber o que é. Os nós são `<button>`, então o Tab já rola a pista sozinho. */
    <div className="trilha-ludica__navegacao">
      {/* As setas são IRMÃS da pista, não filhas: dentro dela seriam conteúdo
          rolável e sairiam de cena junto com o percurso, que é o oposto do que
          precisam fazer. */}
      <SetaDeslizante
        direcao={-1}
        visivel={navegacao.alcance.voltar}
        onClick={() => navegacao.deslizar(-1)}
        rotulo="Ver as fases anteriores"
      />

      <div
        className={`trilha-ludica__pista${
          navegacao.arrastando ? " trilha-ludica__pista--arrastando" : ""
        }${navegacao.movendo ? " trilha-ludica__pista--movendo" : ""}`}
        ref={pistaRef}
        role="region"
        aria-label="Trilha — arraste ou use as setas para ver as outras fases"
        data-testid="trilha-pista"
        {...navegacao.ligacoes}
      >
        <div
          className="trilha-ludica__tabuleiro"
          // Só a largura. O `background-size` fica no CSS, em `auto 100%`, e é o
          // que impede a arte de deformar: a largura do cenário passa a ser sempre
          // a proporção do tile vezes a altura da faixa. Houve aqui um
          // `background-size` inline em porcentagem da faixa, para o cenário
          // esticar junto com os nós — e foi exatamente ele que achatou os cactos.
          style={{ width: `${larguraTabuleiro}px` }}
          data-testid="trilha-tabuleiro"
        >
          {/* Névoa à direita enquanto há leva pela frente — no eixo vertical isto
            era a faixa de nuvem no topo, e antes dela uma pílula "Mais fases pela
            frente" (refino de 29/07 no protótipo): ilustração em vez de rótulo de
            UI sobre a arte.
            O aviso continua existindo em texto, no `aria-label`, porque quem usa
            leitor de tela perdia a informação inteira: uma névoa decorativa não
            diz que a trilha continua. */}
          {sequencial && temFasesEscondidas(faseAtual, totalFases) && (
            <div
              className="trilha-ludica__nevoa-adiante"
              role="img"
              aria-label="Mais fases pela frente"
              data-testid="trilha-nevoa-adiante"
            />
          )}

          <svg
            className="trilha-ludica__caminho"
            viewBox={`0 0 100 ${VIEWBOX_ALTURA}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d={construirPath(pontosSvg)}
              fill="none"
              /* Areia clara puxada das margens de areia do cânion na própria arte
               de fundo: a linha lê como marcação de estrada de terra batida, e
               não como uma camada de UI por cima da ilustração. O branco a 85%
               que estava aqui, e o verde-esmeralda que o protótipo experimentou
               no meio do caminho, competiam com o leito de terracota. */
              stroke="#f7e7cf"
              strokeWidth="8"
              strokeDasharray="6 14"
              strokeLinecap="round"
              strokeLinejoin="round"
              /* O viewBox tem a largura REAL em px e 400 de altura, com
               `preserveAspectRatio="none"` — ou seja o eixo Y é esticado da
               altura da faixa para 400. Sem isto o tracejado sairia achatado
               nesse eixo: `vector-effect` faz a espessura e o padrão do traço
               serem calculados depois da transformação, no espaço da tela. */
              vectorEffect="non-scaling-stroke"
            />
          </svg>

          {posicoes.map(posicao => {
            const numero = posicao.numero;
            const indice = numero - 1;
            const fase = fases[indice];
            const estado = estadoDoNo(indice, fases, sequencial, indiceAtual);

            return (
              <NoTrilha
                key={fase.numero}
                ref={estado === "current" ? noAtualRef : undefined}
                numero={numero}
                titulo={fase.titulo}
                // Campo opcional do contrato: o host que não o manda continua
                // com a estrela genérica em toda fase liberada.
                tipo={fase.tipo}
                estado={estado}
                // Só as fases RARAS ganham o medalhão do mascote, não toda fase
                // premiada: fase de card comum entrega conteúdo, mas não é "prêmio
                // raro" — e com o medalhão em todas elas o mascote aparecia 6 vezes
                // numa trilha de 10, virando padrão de fundo em vez de destaque.
                especial={ehFaseRara(numero, totalFases)}
                // Estrela dourada nas fases CONCLUÍDAS que entregaram card, raras ou
                // comuns: é confirmação do que já foi conquistado, não spoiler — o
                // nó só muda depois de a fase acabar. Substituiu o selinho no canto
                // do nó, que o protótipo tinha e este módulo nunca portou.
                //
                // `faseEntregaCarta` e não `ehFaseComRecompensa` desde 24/08/2026: a
                // segunda diz onde a REGRA põe recompensa, e numa fase rara cuja
                // carta não foi sorteada a estrela afirmaria uma conquista que não
                // houve. O que houve ali foi a CENA do arco, que não é colecionável
                // e não vira estrela. É a mesma regra que monta o álbum, de propósito
                // — se divergirem, o nó e o álbum passam a contar histórias
                // diferentes sobre a mesma fase.
                comCard={faseEntregaCarta(numero, totalFases, sorteio)}
                clicavel={ehClicavel(indice, fases, sequencial)}
                mascoteCabeca={mascoteCabeca}
                esquerdaPct={posicao.esquerdaPct}
                topoPct={posicao.topoPct}
                onAbrir={() => onAbrirFase(fase)}
              />
            );
          })}

          {/* Camada própria, e não filho do nó atual: como filho, o avanço de fase
            desmontava o avatar de um botão e o montava em outro — teleporte, sem
            estado intermediário para animar.

            Sem nó atual (trilha concluída) não há avatar: o explorador marca onde
            o aluno ESTÁ, e quando não há mais para onde ir a marcação perde a
            função.

            `congelado` enquanto o host tem algo por cima. Na passagem de leva o
            avanço não é adiado por congelamento e sim porque a fase que chega ao
            avatar continua sendo a de PARTIDA até a neblina abrir; `semAnimacao`
            então cobre só o que sobra ali, que é o reposicionamento desse nó na
            faixa nova — um deslize de meia faixa que ninguém deve ver. */}
          {avatar && posicaoDoAvatar && (
            <AvatarCaminhante
              avatar={avatar}
              // A FASE é o que dispara a caminhada, e não a coordenada: a
              // coordenada também muda quando a faixa se reparte entre mais nós,
              // e isso não é avanço nenhum. Ver o cabeçalho de AvatarCaminhante.
              fase={faseNaFaixa}
              esquerdaPct={posicaoDoAvatar.esquerdaPct}
              topoPct={posicaoDoAvatar.topoPct}
              espaco={espaco}
              deslocamentoY={-AVATAR_ACIMA_DO_NO}
              congelado={adiarPassagem}
              semAnimacao={passagem !== "nenhuma"}
            />
          )}
        </div>
      </div>

      <SetaDeslizante
        direcao={1}
        visivel={navegacao.alcance.avancar}
        onClick={() => navegacao.deslizar(1)}
        rotulo="Ver as próximas fases"
      />

      {/* Fora do fluxo (portal para o body), mas montado aqui porque é o
          tabuleiro que sabe quando a janela cresceu. Fora da FAIXA, e não dentro,
          porque a faixa rola: um filho dela sairia de cena junto com o percurso. */}
      {passagem === "ativa" && (
        <PassagemLeva onCoberto={trocarLevaCoberto} onFim={fimDaPassagem} />
      )}
    </div>
  );
}

export default Tabuleiro;
